import React, { useState } from "react";
import { Button, Col, Container, Row } from "react-bootstrap";
import ConfirmationPop from "../../../../components/Modals/ConfirmationPop";
import { useGetProductDetailQuery } from "../../../../store/services/ProductStore";
import { useParams, Link } from "react-router-dom";
import {
  useGetRatingsDetailQuery,
  useRatingsStatusMutation,
  useUpdatedRatingsMutation,
} from "../../../../store/services/ratings";

// Star rendering function
const renderStars = (count) => {
  return (
    <span>
      {[...Array(5)].map((_, i) => (
        <span key={i} style={{ color: i < count ? "#FFD700" : "#E0E0E0" }}>
          &#9733;
        </span>
      ))}
    </span>
  );
};

export default function RatingsDetails() {
  const [confirmation, setConfirmation] = useState(false);
  const [actionType, setActionType] = useState(null); // 'approve' or 'reject'
  const [selectedRating, setSelectedRating] = useState(null);
  const [editingComment, setEditingComment] = useState(null);
  const [editCommentText, setEditCommentText] = useState("");
  const { id } = useParams();
  const {
    data: ratings,
    error,
    isLoading,
    refetch,
  } = useGetRatingsDetailQuery(id);
  const [updateStatus, { isLoading: isupdating }] = useRatingsStatusMutation();
  const [updateComment, { isLoading: isUpdatingComment }] = useUpdatedRatingsMutation();

  const handleApprove = (rating) => {
    setActionType("approve");
    setSelectedRating(rating);
    setConfirmation(true);
  };

  const handleReject = (rating) => {
    setActionType("reject");
    setSelectedRating(rating);
    setConfirmation(true);
  };

  const handleConfirm = async () => {
    try {
      const status = actionType === "approve" ? 2 : 3;
      await updateStatus({
        id: selectedRating?._id,
        body: {
          status: status,
        },
      });
      setConfirmation(false);
      setActionType(null);
      setSelectedRating(null);
      refetch();
    } catch (error) {
      console.error("Error updating rating status:", error);
      setConfirmation(false);
      setActionType(null);
      setSelectedRating(null);
    }
  };

  const handleEditComment = (rating) => {
    setEditingComment(rating._id);
    setEditCommentText(rating.comments || "");
  };

  const handleCancelEdit = () => {
    setEditingComment(null);
    setEditCommentText("");
  };

  const handleSaveComment = async (rating) => {
    try {
      await updateComment({
        id: rating._id,
        body: {
          comments: editCommentText,
        },
      });
      setEditingComment(null);
      setEditCommentText("");
      refetch();
    } catch (error) {
      console.error("Error updating comment:", error);
    }
  };

  if (isLoading) {
    return (
      <section className="py-5 position-relative">
        <Container>
          <Row className="justify-content-center">
            <Col md={8} lg={6}>
              <div className="text-center">Loading...</div>
            </Col>
          </Row>
        </Container>
      </section>
    );
  }

  if (error) {
    return (
      <section className="py-5 position-relative">
        <Container>
          <Row className="justify-content-center">
            <Col md={8} lg={6}>
              <div className="text-center text-danger">
                Error loading ratings
              </div>
            </Col>
          </Row>
        </Container>
      </section>
    );
  }

  const ratingsData = ratings?.data || [];

  const firstRating = ratingsData[0];
  const orderDetails = firstRating?.orderId;
  const productReviews = ratingsData.filter((rating) => rating.sizeId);
  const generalReviews = ratingsData.filter((rating) => !rating.sizeId);

  return (
    <>
      <ConfirmationPop
        confirmation={confirmation}
        setConfirmation={setConfirmation}
        confirmHandler={handleConfirm}
        type="confirmation"
      />
      <section className="py-5 position-relative">
        <Container>
          <Row className="justify-content-center">
            <Col md={10} lg={8}>
              {/* Header Section */}
              <div className="mb-4">
                <div className="d-flex align-items-center gap-3 mb-3">
                  <Link
                    to="/manage-store-review"
                    className="border d-flex align-items-center p-2 rounded"
                    style={{
                      backgroundColor: "white",
                      borderColor: "#e9ecef",
                      borderRadius: "8px",
                      textDecoration: "none",
                      transition: "all 0.2s ease-in-out",
                    }}
                    onMouseEnter={(e) => {
                      e.target.style.backgroundColor = "#f8f9fa";
                      e.target.style.transform = "translateY(-1px)";
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.backgroundColor = "white";
                      e.target.style.transform = "translateY(0)";
                    }}
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="12"
                      height="12"
                      viewBox="0 0 10 16"
                      fill="none"
                    >
                      <path
                        d="M8.64707 0.473344C8.55514 0.381188 8.44594 0.308072 8.32572 0.258184C8.20549 0.208296 8.07661 0.182617 7.94644 0.182617C7.81628 0.182617 7.68739 0.208296 7.56717 0.258184C7.44694 0.308072 7.33774 0.381188 7.24582 0.473344L0.667065 7.05209C0.593675 7.12533 0.53545 7.21233 0.495723 7.3081C0.455996 7.40387 0.435547 7.50654 0.435547 7.61022C0.435547 7.7139 0.455996 7.81657 0.495723 7.91234C0.53545 8.00811 0.593675 8.0951 0.667065 8.16834L7.24582 14.7471C7.63373 15.135 8.25915 15.135 8.64707 14.7471C9.03498 14.3592 9.03498 13.7338 8.64707 13.3458L2.9154 7.60626L8.65498 1.86668C9.03498 1.48668 9.03498 0.853344 8.64707 0.473344Z"
                        fill="#1E232C"
                        stroke="#1E232C"
                        strokeWidth="0.2"
                      />
                    </svg>
                  </Link>
                  <h1
                    className="fw-bold text-primary mb-0"
                    style={{ fontSize: "2rem", color: "#6f42c1" }}
                  >
                    Order review details
                  </h1>
                </div>

                {orderDetails && (
                  <div className="mb-3">
                    <div className="text-dark mb-1">
                      Order ID: {orderDetails.orderID}
                    </div>
                    <div className="text-dark mb-1">
                      Email: {firstRating?.userId?.email}
                    </div>
                    <div className="text-dark mb-1">
                      Created At:{" "}
                      {new Date(firstRating?.createdAt).toLocaleDateString()}
                    </div>
                  </div>
                )}

                <hr className="my-3" style={{ borderColor: "#e9ecef" }} />
              </div>

              {/* Products Section */}
              <div>
                <h2 className="fw-bold text-dark mb-3">Products</h2>

                {productReviews.length === 0 && generalReviews.length === 0 ? (
                  <div className="text-center py-4">
                    <p>No product reviews found</p>
                  </div>
                ) : (
                  <>
                    {/* Product-specific reviews */}
                    {productReviews.map((rating, idx) => {
                      const isApproved = rating.status === 2;
                      const isRejected = rating.status === 3;

                      return (
                        <div
                          key={rating._id}
                          className="mb-3 p-4 border rounded bg-white shadow-sm"
                          style={{
                            borderRadius: "8px",
                            boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
                          }}
                        >
                          <div className="d-flex justify-content-between align-items-start">
                            <div className="flex-grow-1">
                              <div className="d-flex align-items-start mb-3">
                                {rating.flavourId.images &&
                                  rating.flavourId.images.length > 0 && (
                                    <div className="me-3">
                                      <img
                                        src={rating.flavourId.images[0]}
                                        alt={rating.flavourId.name}
                                        style={{
                                          width: "80px",
                                          height: "80px",
                                          objectFit: "cover",
                                          borderRadius: "8px",
                                          border: "1px solid #e9ecef",
                                        }}
                                      />
                                    </div>
                                  )}
                                <div className="flex-grow-1">
                                  <h5 className="fw-bold text-dark mb-2">
                                   {rating.flavourId.name}
                                  </h5>
                                  <div className="mb-2">
                                    <span className="text-dark">Rating: </span>
                                    {renderStars(rating.rate || 0)}
                                  </div>
                                  <div className="mb-2">
                                    <span className="fw-bold text-dark">
                                      Comments:{" "}
                                    </span>
                                    {editingComment === rating._id ? (
                                      <div className="d-flex flex-column gap-2">
                                        <textarea
                                          className="form-control"
                                          value={editCommentText}
                                          onChange={(e) => setEditCommentText(e.target.value)}
                                          rows={3}
                                          placeholder="Enter comment..."
                                        />
                                        <div className="d-flex gap-2">
                                          <Button
                                            variant="success"
                                            size="sm"
                                            onClick={() => handleSaveComment(rating)}
                                            disabled={isUpdatingComment}
                                            style={{
                                              backgroundColor: "#28a745",
                                              borderColor: "#28a745",
                                              borderRadius: "6px",
                                              padding: "6px 16px",
                                              fontSize: "12px",
                                              fontWeight: "500",
                                            }}
                                          >
                                            {isUpdatingComment ? "Saving..." : "Save"}
                                          </Button>
                                          <Button
                                            variant="secondary"
                                            size="sm"
                                            onClick={handleCancelEdit}
                                            style={{
                                              backgroundColor: "#6c757d",
                                              borderColor: "#6c757d",
                                              borderRadius: "6px",
                                              padding: "6px 16px",
                                              fontSize: "12px",
                                              fontWeight: "500",
                                            }}
                                          >
                                            Cancel
                                          </Button>
                                        </div>
                                      </div>
                                    ) : (
                                      <div className="d-flex align-items-center gap-2">
                                        <span
                                          className="text-dark"
                                          style={{
                                            wordWrap: "break-word",
                                            overflowWrap: "break-word",
                                            wordBreak: "break-word",
                                            maxWidth: "100%",
                                            display: "inline-block",
                                          }}
                                        >
                                          {rating.comments ||
                                            "No feedback provided"}
                                        </span>
                                        <Button
                                          variant="outline-primary"
                                          size="sm"
                                          onClick={() => handleEditComment(rating)}
                                          style={{
                                            borderRadius: "4px",
                                            padding: "4px 8px",
                                            fontSize: "11px",
                                            minWidth: "auto",
                                          }}
                                        >
                                          Edit
                                        </Button>
                                      </div>
                                    )}
                                  </div>
                                  <div className="d-flex align-items-center gap-3">
                                    <div>
                                      <span className="text-muted">
                                        Price:{" "}
                                      </span>
                                      <span className="text-dark fw-bold">
                                        AED {rating.sizeId.price}
                                      </span>
                                    </div>

                                    {/* {rating.sizeId.discountedPrice && (
                                      <div>
                                        <span className="text-success fw-bold">
                                          AED {rating.sizeId.discountedPrice}
                                        </span>
                                      </div>
                                    )} */}
                                  </div>
                                </div>
                              </div>
                            </div>

                            <div className="ms-3">
                              {isApproved ? (
                                <Button
                                  variant="success"
                                  size="sm"
                                  disabled
                                  style={{
                                    backgroundColor: "#28a745",
                                    borderColor: "#28a745",
                                    borderRadius: "8px",
                                    padding: "8px 20px",
                                    fontSize: "14px",
                                    fontWeight: "500",
                                    boxShadow:
                                      "0 2px 4px rgba(40, 167, 69, 0.2)",
                                  }}
                                >
                                  Approved
                                </Button>
                              ) : isRejected ? (
                                <Button
                                  variant="danger"
                                  size="sm"
                                  disabled
                                  style={{
                                    backgroundColor: "#dc3545",
                                    borderColor: "#dc3545",
                                    borderRadius: "8px",
                                    padding: "8px 20px",
                                    fontSize: "14px",
                                    fontWeight: "500",
                                    boxShadow:
                                      "0 2px 4px rgba(220, 53, 69, 0.2)",
                                  }}
                                >
                                  Rejected
                                </Button>
                              ) : (
                                <div className="d-flex flex-column gap-2">
                                  <Button
                                    variant="success"
                                    size="sm"
                                    onClick={() => handleApprove(rating)}
                                    disabled={
                                      isupdating &&
                                      selectedRating?._id === rating?._id
                                    }
                                    style={{
                                      backgroundColor: "#28a745",
                                      borderColor: "#28a745",
                                      borderRadius: "8px",
                                      padding: "10px 24px",
                                      fontSize: "14px",
                                      fontWeight: "600",
                                      boxShadow:
                                        "0 2px 8px rgba(40, 167, 69, 0.3)",
                                      transition: "all 0.2s ease-in-out",
                                      minWidth: "100px",
                                      border: "none",
                                    }}
                                    onMouseEnter={(e) => {
                                      e.target.style.backgroundColor =
                                        "#218838";
                                      e.target.style.transform =
                                        "translateY(-1px)";
                                      e.target.style.boxShadow =
                                        "0 4px 12px rgba(40, 167, 69, 0.4)";
                                    }}
                                    onMouseLeave={(e) => {
                                      e.target.style.backgroundColor =
                                        "#28a745";
                                      e.target.style.transform =
                                        "translateY(0)";
                                      e.target.style.boxShadow =
                                        "0 2px 8px rgba(40, 167, 69, 0.3)";
                                    }}
                                  >
                                    {isupdating &&
                                    selectedRating?._id === rating?._id
                                      ? "Updating..."
                                      : "Approve"}
                                  </Button>
                                  <Button
                                    variant="danger"
                                    size="sm"
                                    onClick={() => handleReject(rating)}
                                    disabled={
                                      isupdating &&
                                      selectedRating?._id === rating?._id
                                    }
                                    style={{
                                      backgroundColor: "#dc3545",
                                      borderColor: "#dc3545",
                                      borderRadius: "8px",
                                      padding: "10px 24px",
                                      fontSize: "14px",
                                      fontWeight: "600",
                                      boxShadow:
                                        "0 2px 8px rgba(220, 53, 69, 0.3)",
                                      transition: "all 0.2s ease-in-out",
                                      minWidth: "100px",
                                      border: "none",
                                    }}
                                    onMouseEnter={(e) => {
                                      e.target.style.backgroundColor =
                                        "#c82333";
                                      e.target.style.transform =
                                        "translateY(-1px)";
                                      e.target.style.boxShadow =
                                        "0 4px 12px rgba(220, 53, 69, 0.4)";
                                    }}
                                    onMouseLeave={(e) => {
                                      e.target.style.backgroundColor =
                                        "#dc3545";
                                      e.target.style.transform =
                                        "translateY(0)";
                                      e.target.style.boxShadow =
                                        "0 2px 8px rgba(220, 53, 69, 0.3)";
                                    }}
                                  >
                                    {isupdating &&
                                    selectedRating?._id === rating?._id
                                      ? "Updating..."
                                      : "Reject"}
                                  </Button>
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    })}

                    {/* General reviews - no approve/reject functionality */}
                    {generalReviews.length > 0 && (
                      <>
                        <h3 className="fw-bold text-dark mb-3 mt-4">
                          General Reviews
                        </h3>
                        {generalReviews.map((rating, idx) => (
                          <div
                            key={rating._id}
                            className="mb-3 p-4 border rounded bg-white shadow-sm"
                            style={{
                              borderRadius: "8px",
                              boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
                            }}
                          >
                            <div>
                              <h5 className="fw-bold text-dark mb-2">
                                General Review {idx + 1}
                              </h5>
                                                             <div className="mb-2">
                                 <span className="fw-bold text-dark">
                                   Feedback:{" "}
                                 </span>
                                 {editingComment === rating._id ? (
                                   <div className="d-flex flex-column gap-2">
                                     <textarea
                                       className="form-control"
                                       value={editCommentText}
                                       onChange={(e) => setEditCommentText(e.target.value)}
                                       rows={3}
                                       placeholder="Enter feedback..."
                                     />
                                     <div className="d-flex gap-2">
                                       <Button
                                         variant="success"
                                         size="sm"
                                         onClick={() => handleSaveComment(rating)}
                                         disabled={isUpdatingComment}
                                         style={{
                                           backgroundColor: "#28a745",
                                           borderColor: "#28a745",
                                           borderRadius: "6px",
                                           padding: "6px 16px",
                                           fontSize: "12px",
                                           fontWeight: "500",
                                         }}
                                       >
                                         {isUpdatingComment ? "Saving..." : "Save"}
                                       </Button>
                                       <Button
                                         variant="secondary"
                                         size="sm"
                                         onClick={handleCancelEdit}
                                         style={{
                                           backgroundColor: "#6c757d",
                                           borderColor: "#6c757d",
                                           borderRadius: "6px",
                                           padding: "6px 16px",
                                           fontSize: "12px",
                                           fontWeight: "500",
                                         }}
                                       >
                                         Cancel
                                       </Button>
                                     </div>
                                   </div>
                                 ) : (
                                   <div className="d-flex align-items-center gap-2">
                                     <span
                                       className="text-dark"
                                       style={{
                                         wordWrap: "break-word",
                                         overflowWrap: "break-word",
                                         wordBreak: "break-word",
                                         maxWidth: "100%",
                                         display: "inline-block",
                                       }}
                                     >
                                       {rating.comments || "No feedback provided"}
                                     </span>
                                     <Button
                                       variant="outline-primary"
                                       size="sm"
                                       onClick={() => handleEditComment(rating)}
                                       style={{
                                         borderRadius: "4px",
                                         padding: "4px 8px",
                                         fontSize: "11px",
                                         minWidth: "auto",
                                       }}
                                     >
                                       Edit
                                     </Button>
                                   </div>
                                 )}
                               </div>
                              <div className="text-muted small">
                                Reviewed on:{" "}
                                {new Date(
                                  rating.createdAt
                                ).toLocaleDateString()}
                              </div>
                            </div>
                          </div>
                        ))}
                      </>
                    )}
                  </>
                )}
              </div>
            </Col>
          </Row>
        </Container>
      </section>
    </>
  );
}
